<template>
    <b-card no-body>
        <PreviewVpsApprovalModal ref="previewVpsApprovalModal"  @preview-vps-value="previewValue" :approval="current_approval" />
        <VpsPendingApprovalLeadModal ref="vpsPendingApprovalLeadModal" @preview-vps-value="previewValue" :approval="current_approval" @refresh-approval="getPendingApprovals" />
        <LeadDetails ref="leadDetailsModal" @preview-vps-value="previewValue" :approval="current_approval" />
        <b-card-header class="border-0">
            <b-row></b-row>
        </b-card-header>

        <b-row align-v="center" class="px-3 mb-4 mx-0">
            <b-col cols="6" md="auto" class="px-1" v-for="(status, index) in approval_statuses" :key="index">
                <b-button 
                    block
                    variant="outline-primary"
                    :class="getFilterStatusClass(status)"
                    :pressed="selectedStatus === status"
                    class="mb-2 mr-2 shadow-none"
                >
                    {{ status }}<br>
                    <span class="font-weight-600">{{ totalApprovalStatus[status.toLowerCase()] }}</span>
                </b-button>
            </b-col>
        </b-row>

        <b-row align-v="center" class="mx-0">
            <b-col cols="12" md="auto">
                <base-input
                v-model="searchQuery"
                v-on:input="debouncedHandler"
                prepend-icon="fas fa-search"
                placeholder="Search..."
                >
                </base-input>
            </b-col>
        </b-row>

        <div class="table-listings px-3">
            <el-table 
                ref="multipleTable"
                v-loading="isTableLoading"
                :data="tableData"
                style="width: 100%;"
                @selection-change="selectionChange"
            >
                <el-table-column
                    type="selection"
                    width="55"
                    fixed="left"
                ></el-table-column>

                <el-table-column
                    label="Title"
                    min-width="180"
                    sortable
                >
                    <template slot-scope="scope">
                        <p class="top-line text-break">{{ scope.row.appr_content.replace(/Approval \#\d+/, '') | formatUserName(15) }}</p>
                        <p class="bottom-line text-break">{{ scope.row.appr_related_id }}</p>
                    </template>
                </el-table-column>

                <el-table-column
                    label="Customer"
                    min-width="180"
                    sortable
                >
                    <template slot-scope="scope">
                        <p class="top-line text-break">{{ scope.row.appr_req_value.name | formatUserName(25) }}</p>
                        <p class="bottom-line text-break">{{ scope.row.appr_req_value.lead_id }}</p>
                    </template>
                </el-table-column>

                <el-table-column
                    label="Reviewers"
                    min-width="180"
                    sortable
                >
                    <template slot-scope="scope">
                        <div class="d-flex align-items-center">
                            <div v-for="(reviewersStatus, index) in groupingReviewers(scope.row.approval_reviewers)" 
                                :key="index"
                            >
                                <div>
                                    <el-tooltip effect="dark" :content="getReviewerName(scope.row, index)" placement="bottom">
                                        <svg id="status-svg" width="40" height="40">
                                            <circle cx="20" cy="20" r="15" :fill="getStatusFill(reviewersStatus)" />
                                            <text x="20" y="26" text-anchor="middle" fill="#fff" class="reviewer-text">{{ getReviewerInitial(scope.row, index) }}</text>
                                        </svg>
                                    </el-tooltip>
                                </div>
                            </div>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column
                    prop="created_by"
                    label="Created By"
                    min-width="110"
                    align="center"
                >
                    <template slot-scope="scope">
                        <!-- <p>{{ scope.row.created_by | formatUserName(20) }}</p> -->
                        <el-tooltip effect="dark" :content="scope.row.created_by_detail.name" placement="bottom">
                            <b-avatar size="32px" :src="scope.row.created_by_detail.name" :text="getInitials(scope.row.created_by_detail.name)"></b-avatar>
                        </el-tooltip>
                    </template>
                </el-table-column>

                <el-table-column
                    prop="created_at"
                    label="Created At"
                    min-width="150"
                    sortable
                >
                    <template slot-scope="scope">
                        <div v-if="scope.row.created_at">
                            {{ scope.row.created_at | dateFormatString }}
                        </div>
                    </template>
                </el-table-column>

                <el-table-column
                    prop="status"
                    label="Status"
                    min-width="150"
                    sortable
                >
                    <template slot-scope="scope">
                        <svg id="status-svg" width="50" height="50" v-if="scope.row.appr_final_status === null">
                            <circle cx="25" cy="25" r="18" fill="#f3bc43" />
                            <text x="25" y="33" text-anchor="middle" fill="#fff" class="cases-text">P</text>
                        </svg>
                        <svg id="status-svg" width="50" height="50" v-if="scope.row.appr_final_status === 1">
                            <circle cx="25" cy="25" r="18" fill="#2dce89" />
                            <text x="25" y="33" text-anchor="middle" fill="#fff" class="cases-text">A</text>
                        </svg>
                        <svg id="status-svg" width="50" height="50" v-if="scope.row.appr_final_status === 0">
                            <circle cx="25" cy="25" r="18" fill="#f5365c" />
                            <text x="25" y="33" text-anchor="middle" fill="#fff" class="cases-text">R</text>
                        </svg>
                        <svg id="status-svg" width="50" height="50" v-if="scope.row.appr_final_status === 2">
                            <circle cx="25" cy="25" r="18" fill="#00000" />
                            <text x="25" y="33" text-anchor="middle" fill="#fff" class="cases-text">C</text>
                        </svg>
                    </template>
                </el-table-column>

                <el-table-column
                    label="Actions"
                    min-width="150"
                >
                    <template slot-scope="scope">
                        <div class="d-flex">
                            <el-button class="bg-primary text-white p-2" 
                                @click="previewApproval(scope.row)"
                                >Preview
                            </el-button>
                            <el-button class="bg-fintos text-dark p-2" 
                            v-if="scope.row.next_approval_reviewers.length > 0 &&
                                scope.row.next_approval_reviewers.some(reviewer => reviewer.appr_rev_user_id === auth_user.id)"
                                @click="decideApproval(scope.row)"
                                >Decide
                            </el-button>
                        </div>
                    </template>
                </el-table-column>

            </el-table>
        </div>
    </b-card>
</template>
<script>
import { Table, TableColumn, Select, Option, Button, Dropdown, DropdownMenu, DropdownItem, Tooltip } from 'element-ui'
import { BasePagination } from '@/components'
import clientPaginationMixin from '@/assets/js/clientPaginationMixin'
import {
    notifyFunctionMixin,
    axios,
    services,
    getHeader,
    debounce,
    swal
} from '@/assets/js/httpImports.js'
import { APPROVALS_STATUS } from '@/assets/js/status.js'
import PreviewVpsApprovalModal from '@/views/PendingApprovals/VPS/Modals/PreviewVpsApprovalModal'
import LeadDetails from '@/views/PendingApprovals/VPS/Modals/LeadDetailsModal'
import VpsPendingApprovalLeadModal from '@/views/PendingApprovals/VPS/VPSPendingApprovalModals/VpsPendingApprovalLeadModal'
import { useAuthStore } from '@/stores/authStore'
import { useApprovalStore } from '@/stores/approvalStore'
import { mapActions, mapState } from 'pinia'

export default {
    mixins: [notifyFunctionMixin,clientPaginationMixin],
    components: {
        PreviewVpsApprovalModal,
        LeadDetails,
        VpsPendingApprovalLeadModal,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Select.name]: Select,
        [Option.name]: Option,
        [Button.name]: Button,
        [Dropdown.name]: Dropdown,
        [DropdownMenu.name]: DropdownMenu,
        [DropdownItem.name]: DropdownItem,
        [Tooltip.name]: Tooltip,
    },
    data() {
        return {
            isTableLoading: false,
            propsToSearch:[''],
            currentPage: 1,
            selectedRows: [],
            searchText: '',
            debouncedHandler: '',
            TableColumn:[{
                type: 'selection'
            }],
            tableData: [],
            approval_statuses: APPROVALS_STATUS,
            selectedStatus: 'All',
            totalApprovalStatus: {
                all: 0,
                pending: 0,
                approved: 0,
                rejected: 0
            },
            current_approval: null,
            modals: {
                preview_approval: false,
            }
        }
    },
    computed:{
        ...mapState(useAuthStore, ['auth_user']),
    },
    created(){
        this.getPendingApprovals()
    },
    methods: {
        ...mapActions(useApprovalStore, ['reduceCountPendingApproval']),
        selectionChange(selectedRows) {
            this.selectedRows = selectedRows
        },
        paginationChanged(page)
        {
            this.pagination.currentPage = page
        },
        getFilterStatusClass(status) {
            if (status === 'Pending') {
                return 'btn btn-outline-fintos'
            } else if (status === 'Approved') {
                return 'btn btn-outline-success'
            } else if (status === 'Rejected') {
                return 'btn btn-outline-danger'
            }
            return 'btn btn-outline-primary'
        },
        getInitials(name) {
            const names = name.split(' ')
            let initials = ''
            const maxInitials = 3;
            for (let i = 0; i < names.length && i < maxInitials; i++) {
                initials += names[i].charAt(0);
            }
            return initials
        },
        groupingReviewers(approvalReviewers) {
            const grouped_status = {};

            for (const key in approvalReviewers) {
                const item = approvalReviewers[key]

                switch(item.appr_rev_status)
                {
                    case 0:
                        grouped_status[item.app_rev_order] = 0
                        break;
                    case 1:
                        grouped_status[item.app_rev_order] = 1
                        break;
                    case 2:
                        grouped_status[item.app_rev_order] = 2
                        break;
                    case null:
                        if(grouped_status[item.app_rev_order] === undefined)
                        {
                            grouped_status[item.app_rev_order] = null
                        }
                        break;
                }
            }
            return Object.values(grouped_status)
        },
        getReviewerName(row, index) {
            const reviewerNames = row.approval_reviewers
            return reviewerNames[index].appr_rev_user_detail.name || ''
        },
        getReviewerInitial(row, index) {
            const reviewerNames = row.approval_reviewers;
            const name = this.getInitials(reviewerNames[index].appr_rev_user_detail.name)
            return name || '';
        },
        getStatusFill(status) {
            if (status === null) {
                return '#f3bc43'
            } else if (status === 1) {
                return '#2dce89'
            } else if (status === 0) {
                return '#f5365c'
            } else if (status === 2) {
                return '#00000'
            }
            return ''
        },
        previewApproval(approval){
            this.current_approval = approval
            this.$refs.previewVpsApprovalModal.toggleView()
        },
        previewValue(approval_type) {
            this.$refs.leadDetailsModal.toggleView(true)
        },
        getPendingApprovals(){
            this.isTableLoading = true
            axios.get(services.approval + 'approvals', 
            {
                params: {
                    approval_type: "VPS_LEAD_APPROVAL"
                },
                headers: getHeader()
            })
            .then(res => {
                this.tableData = res.data.data
                this.allApprovals = this.tableData

                const countStatus = (status) => this.allApprovals.filter(approval => approval.appr_final_status === status).length
                this.totalApprovalStatus.all = this.allApprovals.length
                this.totalApprovalStatus.pending = countStatus(null)
                this.totalApprovalStatus.approved = countStatus(1)
                this.totalApprovalStatus.rejected = countStatus(0)
                this.isTableLoading = false
                if(!this.is_first_load){
                    this.reduceCountPendingApproval()
                }else{
                    this.is_first_load = false
                }
            })
            .catch(e => {
                this.errorFunction(e)
                this.isTableLoading = false
            })
        },
        decideApproval(approval){
            this.current_approval = approval
            this.$refs.vpsPendingApprovalLeadModal.toggleView(false)
        },
    }
}
</script>